import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Eye, Archive, Trash2, Building2, Banknote, MoreVertical, RotateCcw, Send, Mail } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loading, EmptyState } from "@/components/common/Loading";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, runBulk } from "@/components/common/BulkActionBar";

const SORTS = [
  { value: "recent", label: "Recently updated" },
  { value: "title_asc", label: "Name A–Z" },
  { value: "title_desc", label: "Name Z–A" },
  { value: "status", label: "Status" },
];

export default function MyOrgs({ kind }) {
  const navigate = useNavigate();
  const isCapital = kind === "capital";
  const Icon = isCapital ? Banknote : Building2;
  const title = isCapital ? "My Capital Profiles" : "My Businesses";
  const editBase = isCapital ? "/dashboard/capital-profiles" : "/dashboard/businesses";
  const publicPrefix = isCapital ? "/capital/" : "/b/";
  const noun = isCapital ? "capital profile" : "business";
  const [items, setItems] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Auto-open the create dialog when arriving from a directory "Create" CTA (?new=1).
  const [open, setOpen] = useState(() => !!searchParams.get("new"));
  const [name, setName] = useState("");
  const [sort, setSort] = useState("recent");
  const [bulkDel, setBulkDel] = useState(false);

  // SEC-12: pending team invitations for the signed-in user (self-only endpoint).
  // Drives the "View Invitations (N)" count; the button shows regardless of load state.
  const { data: invData } = useQuery({
    queryKey: ["my-team-invitations"],
    queryFn: async () => (await api.get("/team-invites/mine")).data,
  });
  const inviteCount = invData?.invitations?.length || 0;

  const load = async () => {
    const { data } = await api.get(`/orgs/${kind}/mine`);
    setItems(data);
  };
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await api.get(`/orgs/${kind}/mine`);
      if (!active) return;
      setItems(data);
      if (searchParams.get("new")) {
        searchParams.delete("new");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    const t = (x) => (x.display_name || "").toLowerCase();
    if (sort === "title_asc") arr.sort((a, b) => t(a).localeCompare(t(b)));
    else if (sort === "title_desc") arr.sort((a, b) => t(b).localeCompare(t(a)));
    else if (sort === "status") arr.sort((a, b) => (a.status || "").localeCompare(b.status || "") || t(a).localeCompare(t(b)));
    else arr.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return arr;
  }, [items, sort]);

  const ownedIds = useMemo(() => (items || []).filter((i) => i.is_owner ?? true).map((i) => i.id), [items]);
  const sel = useRowSelection(ownedIds);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post(`/orgs/${kind}`, { display_name: name.trim() });
      setOpen(false); setName("");
      navigate(`${editBase}/${data.id}`);
    } catch { toast.error("Could not create."); }
  };
  const publish = async (it, v) => {
    try { await api.post(`/orgs/${kind}/${it.id}/publish?published=${v}`); toast.success(v ? "Published" : "Unpublished"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const archive = async (it, v) => { await api.post(`/orgs/${kind}/${it.id}/archive?archived=${v}`); toast.success(v ? "Archived" : "Restored"); load(); };
  const remove = async (it) => { await api.delete(`/orgs/${kind}/${it.id}`); toast.success("Deleted"); load(); };

  const bulkPublish = async (v) => { await runBulk(sel.selected, (id) => api.post(`/orgs/${kind}/${id}/publish?published=${v}`), v ? "Published" : "Unpublished"); sel.clear(); load(); };
  const bulkRemove = async () => { await runBulk(sel.selected, (id) => api.delete(`/orgs/${kind}/${id}`), "Deleted"); sel.clear(); setBulkDel(false); load(); };

  if (!items) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">{title}</h1>
          <p className="mt-1 text-muted-foreground">Create, edit, publish and archive your {isCapital ? "capital" : "business"} profiles.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate("/dashboard/invitations")} data-testid="myorgs-view-invitations">
            <Mail className="mr-2 h-4 w-4" /> View Invitations{inviteCount > 0 ? ` (${inviteCount})` : ""}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="org-create-button"><Plus className="mr-2 h-4 w-4" /> New {noun}</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-8"><EmptyState icon={Icon} title={`No ${isCapital ? "capital profiles" : "businesses"} yet`} description="Create your first profile to get started." action={<Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Create one</Button>} testId="org-empty" /></div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} data-testid="mine-select-all" /> Select all
            </label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48" data-testid="mine-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((it) => {
              const role = it.my_role || "owner";
              const isOwn = it.is_owner ?? true;
              const canEdit = role === "owner" || role === "admin";
              const roleTone = role === "owner" ? "bg-teal-50 text-teal-700" : role === "admin" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-700";
              return (
              <Card key={it.id} className={`flex flex-col p-5 ${sel.isSelected(it.id) ? "ring-2 ring-primary" : ""}`} data-testid={`org-card-${it.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {isOwn && <Checkbox className="mt-1" checked={sel.isSelected(it.id)} onCheckedChange={() => sel.toggle(it.id)} data-testid={`select-org-${it.id}`} />}
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-secondary">
                      {it.profile_image_url ? <img src={fileUrl(it.profile_image_url)} alt="" className="h-full w-full object-cover" /> : <Icon className="h-6 w-6 text-muted-foreground" />}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="rounded-md p-1.5 hover:bg-secondary" data-testid={`org-menu-${it.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit && <DropdownMenuItem onClick={() => navigate(`${editBase}/${it.id}`)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>}
                      {it.published && it.slug && <DropdownMenuItem onClick={() => window.open(`${publicPrefix}${it.slug}`, "_blank")}><Eye className="mr-2 h-4 w-4" /> View public</DropdownMenuItem>}
                      {isOwn && (it.status === "archived"
                        ? <DropdownMenuItem onClick={() => archive(it, false)}><RotateCcw className="mr-2 h-4 w-4" /> Restore</DropdownMenuItem>
                        : <DropdownMenuItem onClick={() => archive(it, true)}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="mt-3 font-[640] text-foreground">{it.display_name}</h3>
                {it.headline && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{it.headline}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Badge className={`capitalize ${roleTone}`} data-testid={`org-role-${it.id}`}>{role}</Badge>
                  {it.status === "archived" ? <Badge variant="secondary">Archived</Badge> : it.published ? <Badge className="bg-teal-50 text-teal-700">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  {canEdit ? (
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => navigate(`${editBase}/${it.id}`)} data-testid={`org-edit-${it.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                  ) : it.published && it.slug ? (
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => navigate(`${publicPrefix}${it.slug}`)} data-testid={`org-post-${it.id}`}><Send className="mr-1.5 h-3.5 w-3.5" /> Post update</Button>
                  ) : (
                    <span className="flex-1 text-xs text-muted-foreground">Awaiting publish by the owner.</span>
                  )}
                  {isOwn && it.status !== "archived" && <Button size="sm" variant={it.published ? "ghost" : "default"} onClick={() => publish(it, !it.published)} data-testid={`org-publish-${it.id}`}>{it.published ? "Unpublish" : "Publish"}</Button>}
                  {isOwn && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid={`org-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {it.display_name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(it)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </Card>
            );})}
          </div>
        </>
      )}

      <BulkActionBar count={sel.count} onClear={sel.clear}>
        <Button size="sm" variant="secondary" onClick={() => bulkPublish(true)} data-testid="bulk-publish"><Eye className="mr-1.5 h-4 w-4" /> Publish</Button>
        <Button size="sm" variant="secondary" onClick={() => bulkPublish(false)} data-testid="bulk-unpublish">Unpublish</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkDel(true)} data-testid="bulk-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
      </BulkActionBar>

      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {sel.count} {noun}{sel.count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="bulk-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New {noun}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Display name</Label><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder={isCapital ? "e.g. Otter Ventures" : "e.g. OtterPay"} data-testid="org-create-name" /></div>
          <DialogFooter><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="org-create-confirm">Create & edit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
